/**
 * User Context
 * Provides user profile data and balance management throughout the app
 */

import { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import { UserProfile } from '@trading-sim/shared';
import { api } from '../services/api';
import { useAuthContext } from './AuthContext';

interface UserContextType {
  user: UserProfile | null;
  isLoading: boolean;
  error: string | null;
  fetchUser: () => Promise<void>;
  updateUser: (updates: Partial<UserProfile>) => void;
  updateBalance: (newBalance: number) => void;
  updateDailyIncome: (newDailyIncome: number) => void;
  refreshUser: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuthContext();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUser = useCallback(async () => {
    if (!isAuthenticated) {
      console.log('⏭️ Skipping user fetch - not authenticated');
      return;
    }

    try {
      console.log('👤 Fetching user profile...');
      setIsLoading(true);
      setError(null);
      
      // Add timeout to prevent infinite loading
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );
      
      const response = await Promise.race([
        api.getProfile(),
        timeout
      ]) as any;
      
      console.log('✅ User profile fetched successfully:', response.data);
      setUser(response.data);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch user profile';
      setError(errorMessage);
      console.error('❌ Failed to fetch user:', err);
      
      // If 401, user is not authenticated - clear state
      if (err.response?.status === 401) {
        console.log('🔒 Received 401, clearing user state');
        setUser(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  const updateUser = useCallback((updates: Partial<UserProfile>) => {
    setUser((prevUser) => {
      if (!prevUser) return null;
      return { ...prevUser, ...updates };
    });
  }, []);

  const updateBalance = useCallback((newBalance: number) => {
    setUser((prevUser) => {
      if (!prevUser) return null;
      return { ...prevUser, balance: newBalance };
    });
  }, []);

  const updateDailyIncome = useCallback((newDailyIncome: number) => {
    setUser((prevUser) => {
      if (!prevUser) return null;
      return { ...prevUser, dailyIncome: newDailyIncome };
    });
  }, []);

  const refreshUser = useCallback(async () => {
    return fetchUser();
  }, [fetchUser]);

  // Fetch user when authenticated
  useEffect(() => {
    console.log('🔄 UserContext effect:', { isAuthenticated, hasUser: !!user, isLoading });
    // Only fetch if authenticated and not already loading/loaded
    if (isAuthenticated && !user && !isLoading) {
      console.log('📥 Triggering user fetch...');
      fetchUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, isLoading]);

  return (
    <UserContext.Provider
      value={{
        user,
        isLoading,
        error,
        fetchUser,
        updateUser,
        updateBalance,
        updateDailyIncome,
        refreshUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUserContext() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
}
